import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const intFmt = (n) =>
  new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10);

const COLUMNS = [
  { key: 'productItemName', label: 'Item Name' },
  { key: 'qtySold', label: 'Sale Qty', align: 'right' },
  { key: 'unitPrice', label: 'Item Price', align: 'right' },
];

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
  }),
};

const normalizeRow = (row = {}) => ({
  productItemName:
    String(row.productItemName ?? row.ProductItemName ?? row.itemDescription ?? row.ItemDescription ?? row.itemName ?? row.ItemName ?? '').trim() || '—',
  qtySold: Number(row.qtySold ?? row.QtySold ?? row.saleQty ?? row.Saleqty ?? 0) || 0,
  unitPrice: Number(row.unitPrice ?? row.UnitPrice ?? row.itemPrice ?? row.Itemprice ?? 0) || 0,
});

const TotalProductSaleQtyReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 1,
    hasPrevious: false,
    hasNext: false,
  });

  const now = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  const [searchText, setSearchText] = useState('');
  const [sortState, setSortState] = useState({ key: 'productItemName', dir: 'asc' });

  const pageSizeOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 250, label: '250' },
    { value: 500, label: '500' },
  ];

  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];
      setBranches(
        list.map((b) => ({
          value: b.id,
          label: b.branchName,
        })),
      );
    } catch (error) {
      console.error('Branch Load Error:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_ITEM_MASTER');
      const list = res?.data?.result || [];
      setItems(
        list
          .filter((item) => item?.itemDescription)
          .map((item) => ({
            value: item.itemDescription,
            label: item.itemDescription,
            itemNo: item.itemNo,
          })),
      );
    } catch (error) {
      console.error('Item Load Error:', error);
    }
  };

  const fetchData = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('Res_Branch_Id', selectedBranch?.value || 0);
      params.append('PageNo', page);
      params.append('PageSize', size);

      const itemDescription = selectedItem?.value || searchText.trim();
      if (itemDescription) {
        params.append('itemDescription', itemDescription);
      }

      const res = await apiService.getApiParamater('GET_TOTAL_PRODUCT_SALE_QTY', `?${params.toString()}`);
      const result = Array.isArray(res?.data?.result) ? res.data.result.map(normalizeRow) : [];
      const apiPg = res?.data?.paginationData || {};

      const totalRecords = Number.isFinite(+apiPg.totalRecords) && +apiPg.totalRecords >= 0 ? +apiPg.totalRecords : result.length;
      const pageSizeNorm = Number.isFinite(+apiPg.pageSize) && +apiPg.pageSize > 0 ? +apiPg.pageSize : size;
      const totalPages = Number.isFinite(+apiPg.totalPages) && +apiPg.totalPages > 0 ? +apiPg.totalPages : Math.max(1, Math.ceil(totalRecords / pageSizeNorm));
      const currentPageNorm = Number.isFinite(+apiPg.currentPage) && +apiPg.currentPage > 0 ? +apiPg.currentPage : page;
      const hasPrevious = typeof apiPg.hasPrevious === 'boolean' ? apiPg.hasPrevious : currentPageNorm > 1;
      const hasNext = typeof apiPg.hasNext === 'boolean' ? apiPg.hasNext : currentPageNorm < totalPages;

      setRows(result);
      setPagination({
        currentPage: currentPageNorm,
        pageSize: pageSizeNorm,
        totalRecords,
        totalPages,
        hasPrevious,
        hasNext,
      });
    } catch (error) {
      console.error(error);
      setErr('Failed to fetch product sale qty report. Please try again.');
      setRows([]);
      setPagination((p) => ({
        ...p,
        totalRecords: 0,
        totalPages: 1,
        hasPrevious: false,
        hasNext: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchItems();
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => rows, [rows]);

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...filteredRows];

    copy.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      const na = Number(av);
      const nb = Number(bv);

      if (Number.isFinite(na) && Number.isFinite(nb)) {
        return dir === 'asc' ? na - nb : nb - na;
      }

      return dir === 'asc' ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
    });

    return copy;
  }, [filteredRows, sortState]);

  const totals = useMemo(() => {
    const totalSaleQty = sortedRows.reduce((sum, r) => sum + (+r.qtySold || 0), 0);
    const totalAmount = sortedRows.reduce((sum, r) => sum + (+r.qtySold || 0) * (+r.unitPrice || 0), 0);
    return { totalSaleQty, totalAmount };
  }, [sortedRows]);

  const showingFrom = pagination.totalRecords === 0 ? 0 : (pagination.currentPage - 1) * pagination.pageSize + 1;
  const showingTo = pagination.totalRecords === 0 ? 0 : Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords);

  const onChangeRange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSearch = () => fetchData(1, pagination.pageSize);
  const onPrevPage = () => {
    if (loading || !pagination.hasPrevious) return;
    fetchData(pagination.currentPage - 1, pagination.pageSize);
  };
  const onNextPage = () => {
    if (loading || !pagination.hasNext) return;
    fetchData(pagination.currentPage + 1, pagination.pageSize);
  };
  const onPageSize = (opt) => {
    const newSize = opt?.value || 50;
    fetchData(1, newSize);
  };
  const onSort = (key) => {
    setSortState((prev) => (!prev || prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows
      .map((r) =>
        COLUMNS.map((c) => {
          const val = c.key === 'unitPrice' ? numberFmt(r[c.key]) : c.key === 'qtySold' ? intFmt(r[c.key]) : r[c.key];
          return `"${String(val ?? '').replace(/"/g, '""')}"`;
        }).join(','),
      )
      .join('\n');

    const blob = new Blob([`${headers}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-sale-qty-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    if (!sortedRows.length) return;

    const header = COLUMNS.map((c) => c.label);
    const body = sortedRows.map((r) => [r.productItemName, Number(r.qtySold) || 0, Number(r.unitPrice) || 0]);
    const footer1 = ['Total Sale Qty (this page)', totals.totalSaleQty, ''];
    const footer2 = ['Total Amount (this page)', '', totals.totalAmount];

    const ws = XLSX.utils.aoa_to_sheet([header, ...body, [], footer1, footer2]);
    ws['!cols'] = [{ wch: 40 }, { wch: 14 }, { wch: 14 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Product Sale Qty');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';
    XLSX.writeFile(wb, `product-sale-qty_${branchName}_${formData.fromDate}_to_${formData.toDate}.xlsx`);
  };

  return (
    <div className="p-4">
      <button className="addItem-btn mb-3" type="button" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body">
          <h5 className="mb-3">Total Product Sale Qty Report</h5>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Select Branch</label>
              <Select
                value={selectedBranch}
                onChange={setSelectedBranch}
                options={branches}
                placeholder="All Branches"
                isClearable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Select Item</label>
              <Select
                value={selectedItem}
                onChange={setSelectedItem}
                options={items}
                placeholder="All Items"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">
                {t('From')} {t('Date')}
              </label>
              <input type="date" className="form-control rounded-3" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">
                {t('To')} {t('Date')}
              </label>
              <input type="date" className="form-control rounded-3" name="toDate" value={formData.toDate} onChange={onChangeRange} />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Search Item</label>
              <input
                type="text"
                className="form-control rounded-3"
                placeholder="Search by item name"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="col-12 d-flex flex-wrap gap-2 mt-2 align-items-center">
              <button className="btn btn-primary px-4 rounded-3" type="button" onClick={onSearch} disabled={loading}>
                {loading ? 'Loading…' : 'Search'}
              </button>
              <button className="btn btn-outline-secondary rounded-3" type="button" onClick={exportCSV} disabled={!sortedRows.length}>
                Export CSV
              </button>
              <button className="btn btn-outline-success rounded-3" type="button" onClick={exportExcel} disabled={!sortedRows.length}>
                Export Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="POsgridshad rounded-4 p-3 bg-white shadow-sm">
        {err && <div className="alert alert-danger m-2">{err}</div>}

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
          <div className="text-muted small">
            Showing <b>{intFmt(showingFrom)}</b>–<b>{intFmt(showingTo)}</b> of <b>{intFmt(pagination.totalRecords)}</b>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <label className="text-muted small mb-0">Page size</label>
            <div style={{ minWidth: 130 }}>
              <Select
                value={pageSizeOptions.find((o) => o.value === pagination.pageSize)}
                onChange={onPageSize}
                options={pageSizeOptions}
                isSearchable={false}
                placeholder="Page size"
                styles={selectStyles}
                isDisabled={loading}
              />
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
              Prev
            </button>
            <span className="text-muted small">
              Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
              Next
            </button>
          </div>
        </div>

        <div className="custom-table-container table-responsive" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <table className="custom-table table align-middle mb-0">
            <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                {COLUMNS.map((c) => {
                  const sorted = sortState?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key)}
                      className={`${c.align === 'right' ? 'text-end' : ''} ${sorted ? 'table-active' : ''}`}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {c.label} {sorted ? (sortState.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">
                    Loading…
                  </td>
                </tr>
              ) : sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">
                    No records found
                  </td>
                </tr>
              ) : (
                sortedRows.map((row, index) => (
                  <tr key={`${row.productItemName}-${index}`}>
                    <td>{row.productItemName}</td>
                    <td className="text-end">{intFmt(row.qtySold)}</td>
                    <td className="text-end">{numberFmt(row.unitPrice)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {sortedRows.length > 0 && (
              <tfoot className="table-light fw-semibold">
                <tr>
                  <td>Total (this page)</td>
                  <td className="text-end">{intFmt(totals.totalSaleQty)}</td>
                  <td className="text-end">{numberFmt(totals.totalAmount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(TotalProductSaleQtyReport);
